import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { supabase } from '../supabase/cliente';

export type EventoTabla = 'INSERT' | 'UPDATE' | 'DELETE' | '*';

export interface SuscripcionTiempoReal {
  tabla: string;
  evento?: EventoTabla;
  /** Filtro de PostgREST, por ejemplo `cliente_id=eq.3`. */
  filtro?: string;
  esquema?: string;
}

type Manejador = (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void;

interface CanalRegistrado {
  canal: RealtimeChannel;
  manejadores: Set<Manejador>;
}

/**
 * Punto único de suscripción a cambios en tiempo real (patrón Observer).
 *
 * Resuelve tres problemas de la versión anterior:
 *
 * 1. Cada pantalla tenía que acordarse de desuscribirse a mano; olvidarlo
 *    dejaba canales abiertos consumiendo cuota. Aquí la baja es automática:
 *    `suscribir` devuelve la función que la ejecuta.
 * 2. Dos pantallas interesadas en la misma tabla abrían dos canales. Aquí
 *    comparten uno y se lleva la cuenta de cuántos lo usan.
 * 3. Al renovarse el token de sesión los canales quedaban mudos. Aquí se
 *    renueva la credencial del socket en cuanto cambia.
 */
class ServicioTiempoReal {
  private readonly canales = new Map<string, CanalRegistrado>();

  constructor() {
    // Al renovarse el token hay que actualizar la credencial del socket o las
    // suscripciones dejan de recibir eventos silenciosamente.
    supabase.auth.onAuthStateChange((evento, sesion) => {
      if (evento === 'TOKEN_REFRESHED' || evento === 'SIGNED_IN') {
        supabase.realtime.setAuth(sesion?.access_token ?? null);
      }
      if (evento === 'SIGNED_OUT') {
        this.cerrarTodo();
      }
    });
  }

  /**
   * Escucha los cambios descritos en `config`.
   * @returns función que cancela esta escucha concreta.
   */
  suscribir(config: SuscripcionTiempoReal, manejador: Manejador): () => void {
    const clave = ServicioTiempoReal.clave(config);
    let registro = this.canales.get(clave);

    if (!registro) {
      registro = this.crearCanal(clave, config);
      this.canales.set(clave, registro);
    }

    registro.manejadores.add(manejador);

    let cancelada = false;
    return () => {
      if (cancelada) return;
      cancelada = true;

      const actual = this.canales.get(clave);
      if (!actual) return;

      actual.manejadores.delete(manejador);

      // Sin oyentes el canal ya no tiene razón de existir.
      if (actual.manejadores.size === 0) {
        this.canales.delete(clave);
        void supabase.removeChannel(actual.canal);
      }
    };
  }

  private crearCanal(clave: string, config: SuscripcionTiempoReal): CanalRegistrado {
    const manejadores = new Set<Manejador>();

    const canal = supabase
      .channel(clave)
      .on(
        // El tipado de postgres_changes en el SDK no acepta el objeto genérico
        // que construimos aquí, aunque en tiempo de ejecución es lo esperado.
        'postgres_changes' as never,
        {
          event: config.evento ?? '*',
          schema: config.esquema ?? 'public',
          table: config.tabla,
          ...(config.filtro ? { filter: config.filtro } : {}),
        } as never,
        (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => {
          // Una excepción en un oyente no puede dejar sin evento a los demás.
          for (const manejador of manejadores) {
            try {
              manejador(payload);
            } catch (error) {
              console.error(`[tiempo-real] fallo al procesar ${clave}`, error);
            }
          }
        },
      )
      .subscribe();

    return { canal, manejadores };
  }

  /**
   * Identificador estable del canal.
   * Dos suscripciones con la misma tabla, evento y filtro comparten canal.
   */
  private static clave(config: SuscripcionTiempoReal): string {
    const esquema = config.esquema ?? 'public';
    const evento = config.evento ?? '*';
    const filtro = config.filtro ?? 'sin-filtro';
    return `rt:${esquema}.${config.tabla}:${evento}:${filtro}`;
  }

  /** Cierra todos los canales. Se ejecuta al cerrar sesión. */
  cerrarTodo(): void {
    for (const { canal } of this.canales.values()) {
      void supabase.removeChannel(canal);
    }
    this.canales.clear();
  }

  /** Canales abiertos ahora mismo. Útil para diagnóstico. */
  get canalesAbiertos(): string[] {
    return [...this.canales.keys()];
  }
}

export const servicioTiempoReal = new ServicioTiempoReal();
